import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../../components/feature/SiteHeader";
import SiteFooter from "../../components/feature/SiteFooter";
import PageHero from "../../components/feature/PageHero";
import usePageTitle from "../../hooks/usePageTitle";
import { articles } from "../../mocks/articles";

export default function Analysis() {
  usePageTitle("剧情解析");
  const categories = ["全部", ...Array.from(new Set(articles.map((a) => a.category)))];
  const [active, setActive] = useState("全部");

  const list = useMemo(
    () => (active === "全部" ? articles : articles.filter((a) => a.category === active)),
    [active]
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "守护甜心剧情解析文章",
    description: "守护甜心角色解析、设定解析、结局解析与深度解读文章合集。",
    url: "https://sequelio.github.io/analysis",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sequelio.github.io/" },
        { "@type": "ListItem", position: 2, name: "剧情解析", item: "https://sequelio.github.io/analysis" },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SiteHeader />
      <main>
        <PageHero
          crumbs={[{ label: "首页", path: "/" }, { label: "剧情解析" }]}
          title="剧情解析"
          subtitle="不止讲发生了什么，更帮你读懂为什么。角色、设定、结局与深度解读，全部为你逐层拆解。"
          badge="深度内容 · 持续更新"
        />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-8">
          <div className="flex flex-wrap gap-2" role="tablist">
            {categories.map((c) => {
              const activeCat = active === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={activeCat}
                  onClick={() => setActive(c)}
                  className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                    activeCat
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <Link
                key={a.id}
                to={`/analysis/${a.id}`}
                className="group overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200"
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                      {a.category}
                    </span>
                    <span className="text-xs text-foreground-500">{a.readTime} 阅读</span>
                  </div>
                  <h2 className="mt-3 font-display text-xl leading-snug text-foreground-950 group-hover:text-primary-700">
                    {a.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-foreground-700 line-clamp-2">{a.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {a.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}